/*
 * <fleet-gateway-panel> — the right-side slide-in detail drawer (the design's
 * DetailDrawer, ported to Lit + base-ui). Opens from a gateway card OR a map marker.
 * Reuses base-ui's side-panel mechanics (.side-panel-container--overlay--right +
 * .panel-backdrop, both from styles/base) and the shared statusBadge + ring + confirm
 * dialog. Tabs: Overview / Network / Hardware / Software.
 *
 * Light DOM (createRenderRoot → this) so the shared base-ui SCSS + tokens style it,
 * consistent with <fleet-admin-app>.
 *
 * SAFETY: the footer actions (Unclaim / Revoke) are real super-admin writes to the
 * cloud FleetControl. They are HUMAN-triggered and gated behind <vitesse-confirm-dialog>
 * — never auto-invoked. On success the panel emits `fleet-changed` so the app re-probes.
 */
import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Transport } from '@connectrpc/connect';
import { statusBadge, button, iconButton, pillTabBar, type PillTab } from '@vitesse/base-ui';
// The gate + this app's session. <vitesse-gated> reads the SAME annotation the
// fleet-control handler enforces, so the lock and the refusal cannot disagree.
import '@vitesse/base-ui';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import '@vitesse/base-ui/elements/vitesse-icon';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
// The locked-out viewport for tier-gated tabs. Overview + Software are FLOOR; the
// Hardware + Network tabs need fleet_manager level "standard" (pro), matching the
// fleet UI's graduated capability (overview+software < +hardware+network+alerts < +map).
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import { DASH, cap, statusColor, statusLabel, relativeTime, orgLabel, formatUptime, freshOnly } from '../core/fleet-model';
import { ft, LocaleController } from '../core/i18n';
import {
  type FleetGateway,
  claimGateway,
  unclaimGateway,
  revokeGateway,
  removeGateway,
  setDisplayName,
} from '../core/fleet-client';
import { tabBody, fmtGb, loadPct } from './panel-parts';

type Tab = 'overview' | 'network' | 'hardware' | 'software';
type ActionKind = 'unclaim' | 'revoke' | 'remove';

@customElement('fleet-gateway-panel')
export class FleetGatewayPanel extends LitElement {
  constructor() {
    super();
    new LocaleController(this); // re-render translated text on language switch
  }

  // EVERY tab leads with the SAME 3-dial health row (dials()), so switching tabs
  // shifts nothing — each tab just adds its own unique facts below. Icons match the
  // server panel (folder-kanban / microchip / card-sim); Network keeps ethernet-port.
  private get tabItems(): PillTab[] {
    return [
      { id: 'overview', label: ft('tab.overview'), icon: 'folder-kanban' },
      { id: 'hardware', label: ft('tab.hardware'), icon: 'microchip' },
      { id: 'network', label: ft('tab.network'), icon: 'ethernet-port' },
      { id: 'software', label: ft('tab.software'), icon: 'card-sim' },
    ];
  }

  /** The gateway to show; null → panel closed (renders nothing). */
  @property({ attribute: false }) gateway: FleetGateway | null = null;
  /** FleetControl transport for the footer actions. */
  @property({ attribute: false }) transport!: Transport;
  /** orgId → human name — we never show raw org ids. */
  @property({ attribute: false }) orgNames: Map<string, string> = new Map();

  @state() private tab: Tab = 'overview';
  @state() private action: ActionKind | null = null;
  @state() private busy = false;
  @state() private error = '';
  /** Drives the slide-in: false on mount → true next frame → CSS transform transition. */
  @state() private opened = false;
  /** Fingerprint of the gateway we last (re)initialised for — so a live data refresh
   *  that swaps in a fresh object for the SAME box doesn't reset tab/dialog state. */
  private lastFp = '';
  /** Inline-rename state (the header name is click-to-edit). */
  @state() private editing = false;
  @state() private nameDraft = '';
  @state() private renaming = false;
  /** Remote-claim state — the code input shown in the footer for an unclaimed
   *  box. Distinct from `action`/`runAction`: those wrap the code-free confirm
   *  dialog (unclaim/revoke/remove); claiming needs its own input, not a confirm. */
  @state() private claimCodeDraft = '';
  @state() private claiming = false;
  @state() private claimError = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  updated(changed: Map<string, unknown>): void {
    // Reset to the first tab each time a different gateway opens + (re)play the
    // slide-in: render off-screen (--closed) this frame, flip to --open next frame
    // so the base side-panel transform transition animates it in (same as the app).
    // Guard on the FINGERPRINT, not object identity: the live refresh swaps in a
    // fresh object for the SAME box every cycle — resetting on that slams the tab
    // back to Overview and dismisses the confirm dialog mid-click. Only a genuinely
    // DIFFERENT gateway re-initialises.
    if (changed.has('gateway') && this.gateway && this.gateway.deviceFingerprint !== this.lastFp) {
      this.lastFp = this.gateway.deviceFingerprint;
      this.tab = 'overview';
      this.action = null;
      this.error = '';
      this.claimCodeDraft = '';
      this.claimError = '';
      this.opened = false;
      requestAnimationFrame(() => {
        this.opened = true;
      });
    }
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('panel-close', { bubbles: true, composed: true }));
  }

  private async runAction(): Promise<void> {
    const g = this.gateway;
    const kind = this.action;
    if (!g || !kind) return;
    this.busy = true;
    this.error = '';
    try {
      if (kind === 'unclaim') await unclaimGateway(this.transport, g.deviceFingerprint);
      else if (kind === 'revoke') await revokeGateway(this.transport, g.deviceFingerprint, 'Revoked via fleet admin');
      else await removeGateway(this.transport, g.deviceFingerprint);
      this.busy = false;
      this.action = null;
      this.dispatchEvent(new CustomEvent('fleet-changed', { bubbles: true, composed: true }));
      this.close();
    } catch (err) {
      this.busy = false;
      this.error = err instanceof Error ? err.message : String(err);
    }
  }

  /** Remote/off-LAN claim: fingerprint + the code the box displayed on
   *  enrollment. The LAN quick-claim popup elsewhere in the app covers the
   *  same-network case with no code; this is the only path for anything else
   *  (cloud-hosted, off-site, or across a NAT boundary the net-hash can't see
   *  through) — see fleet-control's Claim RPC (server.go) for the two proofs. */
  private async claim(): Promise<void> {
    const g = this.gateway;
    const code = this.claimCodeDraft.trim();
    if (!g || !code || this.claiming) return;
    this.claiming = true;
    this.claimError = '';
    try {
      await claimGateway(this.transport, g.deviceFingerprint, code);
      this.claiming = false;
      this.claimCodeDraft = '';
      this.dispatchEvent(new CustomEvent('fleet-changed', { bubbles: true, composed: true }));
    } catch (err) {
      this.claiming = false;
      this.claimError = err instanceof Error ? err.message : String(err);
    }
  }

  private startEdit(): void {
    if (!this.gateway) return;
    this.nameDraft = this.gateway.displayName ?? '';
    this.error = '';
    this.editing = true;
  }

  private async saveName(): Promise<void> {
    const g = this.gateway;
    if (!g) return;
    this.renaming = true;
    this.error = '';
    try {
      await setDisplayName(this.transport, g.deviceFingerprint, this.nameDraft.trim());
      this.editing = false; // the live stream reflects the new name within a frame
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
    } finally {
      this.renaming = false;
    }
  }

  // ── KV row (label + value, mono optional, DASH-styled placeholder) ──
  /** Per-tab content = the shared 3-dial row + 4-field grid (panel-parts). Each tab has
   *  its OWN 3 metrics (not a constant row) but the SAME 3+4 structure in the same
   *  positions, so switching tabs never shifts the layout. Metrics not wired to a source
   *  yet render the dial's pending "—" / a DASH field — see the `wire:` notes. */
  /** The active tab's body, tier-gated. Overview + Software are FLOOR (rendered
   *  directly). Hardware + Network sit behind fleet_manager level "standard": the
   *  overlay renders the tab body ONLY when the plan permits it, else a blurred
   *  skeleton with a request/upgrade prompt. There is no per-tab data fetch to
   *  guard — every field renderTab reads is already on the FleetGateway record the
   *  app-level SubscribeFleet stream delivered, so passing the (inert) template as
   *  `.content` leaks nothing: the overlay only mounts it on the allowed path. The
   *  entity path tells an admin WHERE the wall was hit. */
  private renderBody(g: FleetGateway): TemplateResult {
    if (this.tab !== 'hardware' && this.tab !== 'network') return this.renderTab(g);
    return html`<vitesse-gate-overlay
      capability="fleet_manager"
      level="standard"
      context=${`gateways/${g.deviceFingerprint}`}
      .content=${this.renderTab(g)}
    ></vitesse-gate-overlay>`;
  }

  private renderTab(g: FleetGateway): TemplateResult {
    switch (this.tab) {
      // OVERVIEW — the box's vital signs (all real, varying %s).
      case 'overview':
        return tabBody(
          [
            { value: freshOnly(g.healthTsMs, g.cpuPct || null), label: ft('ring.cpu') },
            { value: freshOnly(g.healthTsMs, g.memPct || null), label: ft('ring.mem') },
            { value: freshOnly(g.healthTsMs, loadPct(g.load1, g.cpuCores)), label: ft('server.load') },
          ],
          [
            { label: ft('panel.ownerOrg'), value: orgLabel(g.orgId, this.orgNames) },
            { label: ft('panel.location'), value: g.geo || DASH },
            { label: ft('panel.lastSeen'), value: relativeTime(g.lastSeenMs) },
            { label: ft('panel.connection'), value: g.nodeKind ? cap(g.nodeKind) : DASH },
          ],
        );
      // HARDWARE — temp + disk (load dials) + live process count (activity). Uptime is the
      // real "how long up" — there is NO downtime dial: it was just uptime's inverse, and
      // real windowed downtime needs an offline-event log we don't keep.
      case 'hardware':
        return tabBody(
          [
            { value: freshOnly(g.healthTsMs, g.tempC || null), label: ft('ring.temp'), unit: '°', max: 90 },
            { value: freshOnly(g.healthTsMs, g.diskTotalGb > 0 ? Math.round((g.diskUsedGb / g.diskTotalGb) * 100) : null), label: ft('m.disk') },
            { value: freshOnly(g.healthTsMs, g.processCount || null), label: ft('server.processes'), max: 400, unit: '', tone: 'activity' },
          ],
          [
            { label: ft('server.uptime'), value: formatUptime(g.uptimeS) },
            { label: ft('server.memory'), value: g.memTotalGb > 0 ? `${fmtGb(g.memUsedGb)} / ${fmtGb(g.memTotalGb)}` : DASH },
            { label: ft('m.diskUsed'), value: fmtGb(g.diskUsedGb) },
            { label: ft('m.diskFree'), value: fmtGb(Math.max(0, g.diskTotalGb - g.diskUsedGb)) },
          ],
        );
      // NETWORK — live data-plane activity from the IDS (flows / TCP sessions / inspected
      // pkts-per-sec — real + non-zero on a live net). Packets-lost + attacks are 0-ish →
      // text fields (a 0 reads better as text than as an empty dial).
      case 'network':
        return tabBody(
          [
            { value: freshOnly(g.healthTsMs, g.ids ? g.ids.flowsActive : null), label: ft('m.flows'), max: 2000, unit: '', tone: 'activity' },
            { value: freshOnly(g.healthTsMs, g.ids ? g.ids.tcpSessions : null), label: ft('m.sessions'), max: 1000, unit: '', tone: 'activity' },
            { value: freshOnly(g.healthTsMs, g.ids ? Math.round(g.ids.inspectedPerSec) : null), label: ft('m.pktsPerSec'), max: 10000, unit: '', tone: 'activity' },
          ],
          [
            { label: ft('panel.itIp'), value: g.localIp || DASH, mono: true },
            { label: ft('panel.otIp'), value: g.otIp || DASH, mono: true },
            { label: ft('m.itMac'), value: g.itMac || DASH, mono: true },
            { label: ft('panel.otMac'), value: g.otMac || DASH, mono: true },
          ],
        );
      // SOFTWARE — the running software's resource use: NATS broker throughput + the
      // security engine's own CPU / RSS. Capture-loss is 0-ish → text. os/platform/updater/
      // update are not defined yet → pending text (the agreed exception).
      case 'software':
        return tabBody(
          [
            { value: g.brokerMsgsPerSec > 0 ? Math.min(100, Math.round(g.brokerMsgsPerSec / 20)) : null, label: ft('m.brokerLoad'), tone: 'activity' },
            { value: g.ids?.idsCpuPct || null, label: ft('m.idsCpu') },
            { value: g.ids ? Math.round(g.ids.idsMemMb) : null, label: ft('m.idsMem'), max: 512, unit: '', tone: 'activity' },
          ],
          [
            { label: ft('panel.os'), value: g.os || DASH },
            { label: ft('panel.platform'), value: DASH }, // wire: platform/release version
            { label: ft('panel.updater'), value: DASH }, // wire: updater version
            { label: ft('m.updateStatus'), value: DASH }, // wire: update available?
          ],
        );
    }
  }

  private confirmDialog(g: FleetGateway): TemplateResult {
    const name = g.displayName || ft('dialog.gateway');
    const cfg =
      this.action === 'revoke'
        ? {
            heading: ft('dialog.revokeHeading', { name }),
            message: ft('dialog.revokeMsg'),
            detail: g.deviceFingerprint,
            confirmLabel: ft('btn.revoke'),
            danger: true,
          }
        : this.action === 'remove'
          ? {
              heading: ft('dialog.removeHeading', { name }),
              message: ft('dialog.removeMsg'),
              detail: g.deviceFingerprint,
              confirmLabel: ft('btn.remove'),
              danger: true,
            }
          : {
              heading: ft('dialog.unclaimHeading', { name }),
              message: ft('dialog.unclaimMsg'),
              detail: g.deviceFingerprint,
              confirmLabel: ft('btn.unclaim'),
              danger: false,
            };
    return html`<vitesse-confirm-dialog
      ?open=${this.action != null}
      heading=${cfg.heading}
      message=${cfg.message}
      detail=${cfg.detail}
      confirm-label=${cfg.confirmLabel}
      ?danger=${cfg.danger}
      ?busy=${this.busy}
      error=${this.error}
      @confirm=${() => void this.runAction()}
      @dialog-close=${() => {
        if (!this.busy) this.action = null;
      }}
    ></vitesse-confirm-dialog>`;
  }

  render(): TemplateResult {
    const g = this.gateway;
    if (!g) return html``;
    return html`
      <div class="panel-backdrop ${this.opened ? 'panel-backdrop--visible' : ''}" @click=${this.close}></div>
      <aside
        class="side-panel-container side-panel-container--overlay side-panel-container--right side-panel-container--${this
          .opened
          ? 'open'
          : 'closed'} fleet-gw-panel"
      >
        <div class="fgp-head">
          <div class="fgp-head-row">
            <vitesse-icon name="hard-drive" size="18"></vitesse-icon>
            <div class="fgp-head-main">
              ${this.editing
                ? html`<div class="fgp-rename">
                    <input
                      class="fgp-rename-input"
                      .value=${this.nameDraft}
                      placeholder=${ft('panel.namePlaceholder')}
                      ?disabled=${this.renaming}
                      @input=${(e: Event) => (this.nameDraft = (e.target as HTMLInputElement).value)}
                      @keydown=${(e: KeyboardEvent) => {
                        if (e.key === 'Enter') void this.saveName();
                        else if (e.key === 'Escape') this.editing = false;
                      }}
                    />
                    ${iconButton({ icon: 'check', title: ft('btn.save'), onClick: () => void this.saveName() })}
                    ${iconButton({ icon: 'x', title: ft('btn.cancel'), onClick: () => (this.editing = false) })}
                  </div>`
                : html`<div class="fgp-head-name-row">
                    <div class="fgp-head-name ${g.displayName ? '' : 'is-unnamed'}">
                      ${g.displayName || ft('panel.unnamed')}
                    </div>
                    <vitesse-gated .rpc=${FleetControl.method.setDisplayName} hide>
                      ${iconButton({ icon: 'square-pen', title: ft('hint.rename'), onClick: () => this.startEdit() })}
                    </vitesse-gated>
                  </div>`}
              <div class="fgp-head-id" title=${g.deviceFingerprint}>${g.deviceFingerprint}</div>
            </div>
            ${iconButton({ icon: 'x', title: ft('btn.close'), onClick: () => this.close() })}
          </div>
          <div class="fgp-badges">
            ${statusBadge(statusColor(g.status), statusLabel(g.status))}
            ${g.orgId ? statusBadge('blue', ft('status.claimed')) : statusBadge('gray', ft('status.unclaimed'))}
            ${g.revoked ? statusBadge('red', ft('status.revoked')) : nothing}
            ${g.geo ? statusBadge('gray', g.geo) : nothing}
            ${g.nodeKind ? statusBadge('gray', cap(g.nodeKind)) : nothing}
          </div>
        </div>

        <div class="fgp-tabs">
          ${pillTabBar({
            tabs: this.tabItems,
            active: this.tab,
            onChange: (id) => (this.tab = id as Tab),
          })}
        </div>

        <div class="fgp-body">${this.renderBody(g)}</div>

        <div class="fgp-footer">
          ${g.orgId
            ? html`<vitesse-gated .rpc=${FleetControl.method.unclaim} inline>
                ${button({ label: ft('btn.unclaim'), title: ft('hint.unclaim'), icon: 'unplug', color: 'red', onClick: () => (this.action = 'unclaim') })}
              </vitesse-gated>`
            : html`<vitesse-gated .rpc=${FleetControl.method.claim} inline>
                <div class="fgp-claim" title=${ft('hint.claim')}>
                  <div class="fgp-claim-row">
                    <input
                      class="fgp-claim-input"
                      .value=${this.claimCodeDraft}
                      placeholder=${ft('claim.codePlaceholder')}
                      ?disabled=${this.claiming}
                      @input=${(e: Event) => (this.claimCodeDraft = (e.target as HTMLInputElement).value.toUpperCase())}
                      @keydown=${(e: KeyboardEvent) => {
                        if (e.key === 'Enter') void this.claim();
                      }}
                    />
                    ${button({
                      label: ft('btn.claim'),
                      title: ft('hint.claim'),
                      icon: 'plug',
                      onClick: () => void this.claim(),
                      disabled: this.claiming || !this.claimCodeDraft.trim(),
                    })}
                  </div>
                  ${this.claimError ? html`<div class="fgp-claim-error">${ft('claim.failed', { message: this.claimError })}</div>` : nothing}
                </div>
              </vitesse-gated>`}
          <!--
            Revoke is HIDDEN rather than locked, and it is the one control here
            that should be. It is a Vitesse platform-operator kill-switch, so it
            is not something an org admin can be granted or can buy — a lock
            reading "upgrade to revoke" would be a lie, and a remedy that does not
            exist is worse than no control at all.

            The hide attribute on vitesse-gated now reads the RPC's own
            super_admin annotation — the same declaration the server refuses on. It
            used to ask session.isSuperAdmin, with a comment explaining that the
            server checked this "inside the handler rather than via a permission"
            — true when written, and the reason five platform verbs sat in the
            policy report as deliberately ungated. The annotation says it now, so
            the control and the refusal read the same source.
          -->
          <vitesse-gated .rpc=${FleetControl.method.revoke} hide>
            ${button({ label: ft('btn.revoke'), title: ft('hint.revoke'), icon: 'shield-check', color: 'red', onClick: () => (this.action = 'revoke') })}
          </vitesse-gated>
          <vitesse-gated .rpc=${FleetControl.method.removeGateway} inline>
            ${button({ label: ft('btn.remove'), title: ft('hint.remove'), icon: 'trash-2', color: 'red', onClick: () => (this.action = 'remove') })}
          </vitesse-gated>
        </div>
      </aside>
      ${this.confirmDialog(g)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-gateway-panel': FleetGatewayPanel;
  }
}
